import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { BusListener } from 'src/app/shared/decorators/comp.decorator';
import { HeaderDetail, HeaderDetailButton, HeaderDetailDefaultButtons, HeaderDetailDefaultButtonsDialog, ListenerDetailAction, ListenerDialogAction, ListenerListAction, MessageBusListener, ResponseConfirmation } from 'src/app/shared/global';
import { BusService } from 'src/app/shared/services/bus.service';
import { TinMessageService } from 'src/app/shared/services/tin-message.service';

@BusListener(MessageBusListener.HeaderDetail)
@Component({
  selector: 'app-header-detail',
  templateUrl: './header-detail.component.html',
  styleUrls: ['./header-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderDetailComponent implements OnDestroy, OnInit{

  @Input() title: string = '';
  @Input() showCloseButton: boolean = true;
  @Input() showPreviousNextButtons: boolean = false;
  @Input() showExpandButton: boolean = true;
  @Input() showMoreButton: boolean = true;
  @Input() disableLoadDefaultButtons: boolean = false;
  @Input() dialogId: string = "";
  @Input() buttons!: HeaderDetailButton[];
  

  isExpanded = false;
  actionsButtonMore!: HeaderDetailButton[];
  partialTitle = '';
  isGridFirstRow: boolean = false;
  isGridLastRow: boolean = false;
  gridIndex: number = 1;

  @ViewChild('titleDiv') titleDiv!: ElementRef<any>;

  private subs = new Subscription();
  private lastHeaderDetail: any = {};
  private _lastActionTimestamp = 0;

  constructor(
    private messageService: TinMessageService,
    public serviceBus: BusService,
    private changeDetectorRef: ChangeDetectorRef,
    )
    { }
  
  ngOnInit(): void {
    this.setDefaultValues();
  }

  _new() {
    if (!this.dialogId ) {
      this.title = '';
      this.showExpandButton = false;
      this.showMoreButton = false;
      this.showPreviousNextButtons = false;
      this._changeHeaderDetail({buttons:[
        {actionName: ListenerDetailAction.New, visible: false},
        {actionName: ListenerDetailAction.SaveAndClose, visible: false},
        {actionName: ListenerDetailAction.Delete, visible: false}
      ]}, undefined, false);
    }
  }

  _returnLastConfig() {
    this.buttons = JSON.parse(JSON.stringify(this.lastHeaderDetail.buttons));
    this.showExpandButton = this.lastHeaderDetail.showExpandButton;
    this.showCloseButton = this.lastHeaderDetail.showCloseButton;
    this.showPreviousNextButtons =  this.lastHeaderDetail.showPreviousNextButtons;
    this.showMoreButton = this.lastHeaderDetail.showMoreButton;
  }

  _readonly() {
    this._changeHeaderDetail({buttons:[
      {actionName: ListenerDetailAction.New, visible: false},
      {actionName: ListenerDetailAction.Save, visible: false},
      {actionName: ListenerDetailAction.SaveAndClose, visible: false},
      {actionName: ListenerDetailAction.Delete, visible: false},
      {actionName: ListenerDetailAction.Audit, visible: false},
      {actionName: ListenerDetailAction.Cancel, visible: false},
    ]}, undefined, false);
  }


  _formIsDirtyChanged(data: any, dialogId: string) {
    this.dialogId ?
      this._changeHeaderDetail({buttons:[
        {actionName: ListenerDialogAction.SaveAndClose, enabled: data.isDirty},
        {actionName: ListenerDialogAction.Save, enabled: data.isDirty},
        {actionName: ListenerDialogAction.Ok, enabled: data.isDirty},
        {actionName: ListenerDialogAction.Cancel, enabled: data.isDirty},
      ]}, dialogId, false) :
      this._changeHeaderDetail({buttons:[
        {actionName: ListenerDetailAction.Save, enabled: data.isDirty},
        {actionName: ListenerDetailAction.SaveAndClose, enabled: data.isDirty},
        {actionName: ListenerDetailAction.Cancel, enabled: data.isDirty},
      ]}, dialogId, false);
  }

  getPermissionMore(){
     let permissionMore: string[] = [];
    this.buttons?.filter(x => x.isActionButtonMore)?.forEach(b => {
      permissionMore = permissionMore.concat(b.permissions || []);
    });
    return permissionMore;
  }
  getButtons(): any[] {
    return this.buttons?.filter(x => !x.isActionButtonMore && x.visible) || [];
  }
  getActionsButtonMore(): any[] {
    return this.buttons?.filter(x => x.isActionButtonMore && x.visible && this.showMoreButton) || [];
  }

  toggleExpandDetails(data: any): void {
    if (this.isExpanded) {
      this.dialogId ? this.serviceBus.sendMessageToDialog(ListenerDialogAction.Shrink, this.dialogId)
      : this.serviceBus.sendMessageToList(ListenerListAction.Shrink);
    }
    else {
      this.dialogId ? this.serviceBus.sendMessageToDialog(ListenerDialogAction.Expand, this.dialogId)
      : this.serviceBus.sendMessageToList(ListenerListAction.Expand);
    }
    this.isExpanded = !this.isExpanded;
    this.changeDetectorRef.detectChanges();
  }
  sendActionMessage(actionName: any) {
    if (actionName !== ListenerDetailAction.Delete) {
      const now = Date.now();
      if (now - this._lastActionTimestamp < 500) {
        return;
      }
      this._lastActionTimestamp = now;
    }

    if (actionName ==  ListenerDetailAction.Delete) {
      this.subs.add(this.messageService.openDialogConfirmation('global.delete', 'dialog.confirmationDelete').subscribe(x => {
        if (x && x.response === ResponseConfirmation.Yes) {
          this.dialogId ? this.serviceBus.sendMessageToDialog(actionName, this.dialogId) : this.serviceBus.sendMessageToDetail(actionName);
        }
      }));
    } else {
      this.dialogId ? this.serviceBus.sendMessageToDialog(actionName, this.dialogId) : this.serviceBus.sendMessageToDetail(actionName);
    }
  }

  _changeHeaderDetail(headerDetail: HeaderDetail, dialogId?: string, updateLastHeaderDetails = true) {
    if (headerDetail && ((this.dialogId && this.dialogId === dialogId) || (this.dialogId === "" && !dialogId))) {
      this.title = headerDetail.title ? headerDetail.title : this.title;
      this.showCloseButton = this.getDefaultBool(this.showCloseButton, headerDetail.showCloseButton);
      this.showPreviousNextButtons = this.getDefaultBool(false, this.showPreviousNextButtons);
      this.showExpandButton = this.getDefaultBool(this.showExpandButton, headerDetail.showExpandButton);
      this.showMoreButton = this.getDefaultBool(this.showMoreButton, headerDetail.showMoreButton);
      headerDetail?.buttons?.forEach(btn => {
        if (btn.actionName !== '') {
          const item: any = this.buttons?.find(x => x.actionName === btn.actionName);
          if (item) {
            Object.assign(item, btn);
          } else {
            this.buttons.push(btn);
          }
        }
      });
      if (updateLastHeaderDetails) {
        this.updateLastHeaderDetails();
      }
      this.changeDetectorRef.detectChanges();
    }
  }
  setDefaultValues() {
    const buttons = [];
    this.showExpandButton ??= true;
    this.showCloseButton ??= true;
    this.showMoreButton  ??= this.dialogId === "";
    if (!this.disableLoadDefaultButtons) {
      if (this.dialogId) {
        buttons.push(HeaderDetailDefaultButtonsDialog.Ok, HeaderDetailDefaultButtonsDialog.Cancel,
          HeaderDetailDefaultButtonsDialog.New, HeaderDetailDefaultButtonsDialog.Delete);
       } else {
        buttons.push(HeaderDetailDefaultButtons.SaveAndClose, HeaderDetailDefaultButtons.Save, 
          HeaderDetailDefaultButtons.Cancel, HeaderDetailDefaultButtons.Delete,
          HeaderDetailDefaultButtons.Audit);
       }
    }
     this.buttons ??= JSON.parse(JSON.stringify(buttons));
     this.updateLastHeaderDetails();
  }
  updateLastHeaderDetails(){
    this.lastHeaderDetail.buttons = JSON.parse(JSON.stringify(this.buttons));
    this.lastHeaderDetail.showExpandButton = this.showExpandButton;
    this.lastHeaderDetail.showCloseButton = this.showCloseButton;
    this.lastHeaderDetail.showMoreButton = this.showMoreButton;
    this.lastHeaderDetail.showPreviousNextButtons = this.showPreviousNextButtons;
  }

  get titleCalc(): string  {
    const el: any = (<any>this.titleDiv)?.['nativeElement']
    if (el) {
      const width = el.offsetWidth; 
      const calc = width * 9/100;
      return this.title ? this.title.toString().substring(0, calc).concat(this.title.length > calc ? "..." : "") : '';
    } else {
      setTimeout(() => {
        this.changeDetectorRef.detectChanges(); //forcer la détection des changements lors de l'utilisation de dialogue
      }, 100);
      return '';
    }
  } 

  _selectedLineGridChange(data: any){
    this.isGridFirstRow = data.isGridFirstLine || false;
    this.isGridLastRow = data.isGridLastLine || false;
    this.gridIndex = (data.gridIndex || 0) + 1;
    this.changeDetectorRef.detectChanges();
  }

  previousItem() {
    this.serviceBus.sendMessageToList(ListenerListAction.PreviousNext, {action: 'Previous'});
  }

  nextItem() {
    this.serviceBus.sendMessageToList(ListenerListAction.PreviousNext, {action: 'Next'});
  }

  getDefaultBool(out: boolean, item: any): boolean {
    return item === undefined ? out : item;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
